/**
 * Chatwoot / FrontDesk Statuses Service
 *
 * CRUD + reorder for conversation status definitions (Settings → Statuses).
 * Paths use /api/accounts/statuses (no accountId in URL; JWT scopes print house).
 */

import { BaseChatwootService } from "../core/base-chatwoot-service";
import type {
  AutoStatusUpdateInput,
  AutoStatusUpdateSettings,
  ConversationStatusDefinition,
  CreateStatusPayload,
  ReorderStatusesPayload,
  StatusDetailResponse,
  StatusListResponse,
  UpdateStatusPayload,
} from "../types/statuses.types";

export class ChatwootStatusesService extends BaseChatwootService {
  constructor(baseUrl?: string) {
    super(baseUrl);
  }

  async listStatuses(
    _accountId?: number
  ): Promise<ConversationStatusDefinition[]> {
    const response = await this.get<StatusListResponse>(
      `/api/accounts/statuses`
    );
    if (Array.isArray(response?.data?.payload)) {
      return response.data.payload;
    }
    if (Array.isArray(response?.payload)) {
      return response.payload;
    }
    if (Array.isArray(response)) {
      return response as ConversationStatusDefinition[];
    }
    return [];
  }

  async getStatus(
    _accountId: number,
    statusId: number | string
  ): Promise<ConversationStatusDefinition> {
    const response = await this.get<StatusDetailResponse>(
      `/api/accounts/statuses/${statusId}`
    );
    if (response?.data?.payload) return response.data.payload;
    if (response?.payload) return response.payload;
    return response as ConversationStatusDefinition;
  }

  async createStatus(
    _accountId: number,
    payload: CreateStatusPayload
  ): Promise<ConversationStatusDefinition> {
    const response = await this.post<StatusDetailResponse>(
      `/api/accounts/statuses`,
      { status: payload }
    );
    if (response?.data?.payload) return response.data.payload;
    if (response?.payload) return response.payload;
    return response as ConversationStatusDefinition;
  }

  async updateStatus(
    _accountId: number,
    statusId: number | string,
    payload: UpdateStatusPayload
  ): Promise<ConversationStatusDefinition> {
    const response = await this.put<StatusDetailResponse>(
      `/api/accounts/statuses/${statusId}`,
      { status: payload }
    );
    if (response?.data?.payload) return response.data.payload;
    if (response?.payload) return response.payload;
    return response as ConversationStatusDefinition;
  }

  async reorderStatuses(
    _accountId: number,
    payload: ReorderStatusesPayload
  ): Promise<ConversationStatusDefinition[]> {
    const response = await this.post<StatusListResponse>(
      `/api/accounts/statuses/reorder`,
      { statusIds: payload.statusIds }
    );
    if (Array.isArray(response?.data?.payload)) {
      return response.data.payload;
    }
    if (Array.isArray(response?.payload)) {
      return response.payload;
    }
    return [];
  }

  async deleteStatus(
    _accountId: number,
    statusId: number | string
  ): Promise<void> {
    await this.delete(`/api/accounts/statuses/${statusId}`);
  }

  /**
   * Status applied after a GoMake ERP FrontDesk send (disabled by default).
   * GET /api/accounts/statuses/auto_update
   */
  async getAutoUpdateSettings(): Promise<AutoStatusUpdateSettings> {
    const response = await this.get<{ payload: AutoStatusUpdateSettings }>(
      `/api/accounts/statuses/auto_update`
    );
    return response.payload;
  }

  /**
   * 400 bodies carry `{ code, message }` (STATUS_REQUIRED, STATUS_INACTIVE, STATUS_NOT_CUSTOM).
   * PUT /api/accounts/statuses/auto_update
   */
  async saveAutoUpdateSettings(
    input: AutoStatusUpdateInput
  ): Promise<AutoStatusUpdateSettings> {
    const response = await this.put<{ payload: AutoStatusUpdateSettings }>(
      `/api/accounts/statuses/auto_update`,
      input
    );
    return response.payload;
  }
}
